import type { MutableRefObject } from 'react';
import type { Crepe } from '@milkdown/crepe';
import { collabServiceCtx, type CollabService } from '@milkdown/plugin-collab';
import { editorViewCtx } from '@milkdown/kit/core';
import type * as Y from 'yjs';
import type { Awareness } from 'y-protocols/awareness';

import { isRemoteTransaction } from '../../collaboration/isRemoteTransaction';
import { normalizeMarkdown } from '../../normalizeMarkdown';
import { setupCollabContentChangeGate } from './setupCollabContentChangeGate';
import type { PostDispatchFn } from '../../useEditorReady';

export interface CollabState {
  ydoc: Y.Doc;
  awareness: Awareness;
}

/**
 * Binds the editor to a Y.Doc for real-time collaboration.
 * Connects the collab service after the editor mounts.
 * Stores collabService in serviceRef so it can be disconnected before editor teardown.
 *
 * Single dispatch wrapper (when postDispatchRef provided): swallows "not found" teardown
 * errors and invokes postDispatchRef after each dispatch. useEditorReady registers
 * its heading/tracking logic via this ref instead of layering a second wrapper.
 *
 * Initial markdown is NOT applied here. Milkdown `applyTemplate` targets the wrong
 * fragment name for Platform (`prosemirror` vs `default`), and applying before remote
 * sync races with server state (duplicate content). Collaborative documents are seeded
 * from the Hocuspocus/Y.Doc server state.
 */
export function setupCollaboration(
  crepe: Crepe,
  ydoc: Y.Doc,
  awareness: Awareness,
  _initialMarkdown: string,
  serviceRef: MutableRefObject<CollabService | null>,
  postDispatchRef: MutableRefObject<PostDispatchFn | null>,
  localEditPendingRef: MutableRefObject<boolean>,
  onReadyRef?: MutableRefObject<(() => void) | undefined>,
) {
  crepe.on((listener) => {
    listener.mounted((ctx) => {
      // The async review-suggestions plugin gates editor-view creation on a network
      // fetch, widening the window in which the editor can be torn down/re-created
      // (StrictMode double-mount, room switch) while this handler runs. Reading or
      // reconfiguring a removed editorView ctx throws "not found"; swallow that so a
      // dead mount no-ops instead of surfacing an error. connect() itself reads
      // editorViewCtx internally, so it must be inside this guard too.
      //
      // NOTE: the whole body is treated as ctx-teardown-equivalent — bind/setAwareness
      // assume a live ctx. The narrow "not found" message match keeps any unrelated
      // error propagating, so a genuinely-bad bind still surfaces.
      try {
        const collabService = ctx.get(collabServiceCtx);
        serviceRef.current = collabService;

        // Get the XmlFragment with 'default' name to match Platform's server
        const xmlFragment = ydoc.getXmlFragment('default');

        // Bind fragment and awareness
        collabService.bindXmlFragment(xmlFragment);
        collabService.setAwareness(awareness);

        // Dispatch wrapper: teardown guard, post-dispatch hooks, preview handoff on first docChanged.
        let awaitingHandoff = Boolean(onReadyRef);
        let handoffRafId: number | null = null;

        const cancelPendingHandoff = () => {
          awaitingHandoff = false;
          if (handoffRafId !== null) {
            cancelAnimationFrame(handoffRafId);
            handoffRafId = null;
          }
        };
        const completeHandoff = () => {
          if (!awaitingHandoff) return;
          cancelPendingHandoff();
          onReadyRef?.current?.();
        };

        const view = ctx.get(editorViewCtx);
        if (view) {
          const origDispatch = view.dispatch.bind(view);
          view.dispatch = (tr) => {
            try {
              const result = origDispatch(tr);
              if (tr.docChanged && !isRemoteTransaction(tr)) {
                localEditPendingRef.current = true;
              }
              postDispatchRef.current?.(tr);
              if (awaitingHandoff && tr.docChanged) {
                completeHandoff();
              }

              return result;
            } catch (e) {
              if (e instanceof Error && e.message.includes('not found')) {
                return undefined;
              }
              throw e;
            }
          };
        }

        collabService.connect();

        // Fallback when connect applies without a docChanged transaction (e.g. empty doc).
        if (awaitingHandoff) {
          queueMicrotask(() => {
            if (!awaitingHandoff) return;
            handoffRafId = requestAnimationFrame(completeHandoff);
          });
        }

        return cancelPendingHandoff;
      } catch (e) {
        if (e instanceof Error && e.message.includes('not found')) {
          return;
        }
        throw e;
      }
    });
  });
}

/**
 * Subscribe to serialized markdown updates and invoke the callback (collab dirty tracking, Storybook, etc.).
 *
 * In collab mode, only local edits propagate — remote Yjs sync must not mutate platform draftText.
 */
export function setupCollabContentChange(
  crepe: Crepe,
  onContentChangeRef: React.MutableRefObject<((markdown: string) => void) | undefined>,
  localEditPendingRef?: MutableRefObject<boolean>,
) {
  crepe.on((listener) => {
    const notifyContentChange = localEditPendingRef
      ? setupCollabContentChangeGate(localEditPendingRef, (md) => {
          onContentChangeRef.current?.(md);
        })
      : (md: string) => {
          onContentChangeRef.current?.(md);
        };

    listener.markdownUpdated((_ctx, md: string) => {
      notifyContentChange(normalizeMarkdown(md));
    });
  });
}
